// THE NAVBAR'S PLACE (Nika 2026-10-24: "on both workspaces - com and normal
// make sidebar optional to be positioned at the top left right or bottom. for
// normal workspace keep as is. for com - move to the left. left and right
// sidebars can be toggle expandable"; 2026-10-27: "also make navbar left by
// default").
//
// The header (components/AppHeader.js) is the navbar on both hosts. A person
// places it at the top, on the left, on the right or at the bottom — a choice
// of their own look (workspace functions/homeLook.js), one per host: the
// workspace host's (`navbarPosition`) and the comm host's
// (`commNavbarPosition`). Both open on a left rail by default (the workspace
// host kept its top navbar until 2026-10-27). A left or right rail shows the
// glyphs only, or the glyphs and their names (`…Expanded`). A phone keeps the
// shell's own posture (the header strip and the dock) whatever the choice:
// the layout reads 'top' there.
//
// A host without the look (the canvas or the preview app alone) has its
// navbar at the top: the look functions are read through guarded doors, so it
// reads the defaults without a "not registered" call.
//
// Reactive: the factories that read this subscribe through what it reads —
// the look (`_homeLook`, `userPrefs`) and the phone flag. Every key is read on
// every run, before any decision (a factory that returns before it reads a
// key never re-runs on that key).
//
// FRANK: every export is self-contained (FA204) — the vocabulary and the
// phone query are restated where they are used; siblings through `this.call`.

// → { position: 'top' | 'left' | 'right' | 'bottom', rail, railMode, bottom,
//     expanded, phone, placeable, sidebarSide } — `railMode`: 'narrow' |
//     'wide' on a rail, null off it (the header's `data-nav-rail` attribute —
//     null removes it); `phone`: the shell's own phone posture holds, the
//     choice waits; `placeable`: this host's frame honours the choice;
//     `sidebarSide`: where a page's own sidebar sits (THE PAGE SIDEBAR'S SIDE
//     below)
//
// THE PAGE SIDEBAR'S SIDE (Nika 2026-10-29: "move sidebar at the right - if
// navbar is at the left, and opposite. for top and bottom - keep them"): a
// page's own sidebar (AppSidebar, functions/pageSidebar.js) sits on the side
// the navbar does NOT take — 'right' beside a left rail, 'left' beside a right
// rail, and 'left' (as ever) under a top bar, above a bottom bar, on a phone
// and on a host without the look. So a page sidebar never shares an edge with
// the navbar, and never needs the navbar's inset.
export const navbarLayoutRead = function navbarLayoutRead(root) {
  const r = root && typeof root === 'object' ? root : {}
  const phoneFlag = r._isPhoneViewport
  const fns = this && this.context && this.context.functions
  const look = (fns && typeof fns.homeLookRead === 'function' && this.call('homeLookRead', r)) || {}
  const comm = !!(fns && typeof fns.isCommHost === 'function' && this.call('isCommHost'))
  let phone = phoneFlag === true
  // before the shell's first viewport read the flag is still null: ask the
  // phone query itself (the brand `mobileL`)
  if (phoneFlag == null && typeof window !== 'undefined' && window.matchMedia) {
    try {
      phone = window.matchMedia('(max-width: 768px)').matches
    } catch (_) {
      phone = false
    }
  }
  // PLACEABLE: the host's frame places a rail or a bottom bar — both hosts
  // that carry the look: the comm app's grid (workspace comm/app.js) and the
  // workspace shell's frame (workspace app.js, "THE NAVBAR'S PLACE": its
  // content, PageSlot, composer, drawers and page rails clear the bar where
  // it is). A host without the look (the canvas or the preview app alone)
  // keeps the top and offers no choice.
  const placeable = !!(
    fns &&
    typeof fns.homeLookRead === 'function' &&
    typeof fns.homeLookSet === 'function'
  )
  const chosen = comm ? look.commNavbarPosition : look.navbarPosition
  const valid = ['top', 'left', 'right', 'bottom'].includes(chosen)
  const position = phone || !placeable ? 'top' : valid ? chosen : 'left'
  const rail = position === 'left' || position === 'right'
  const open = comm ? look.commNavbarExpanded : look.navbarExpanded
  const expanded = rail && open === true
  return {
    position,
    rail,
    railMode: rail ? (expanded ? 'wide' : 'narrow') : null,
    bottom: position === 'bottom',
    expanded,
    phone,
    placeable,
    sidebarSide: position === 'left' ? 'right' : 'left'
  }
}

// A decision: `el.call('navbarLayoutSet', { position: 'left' })` or
// `{ expanded: true }` — this host's own keys, through the look's one writer
// (homeLookSet: the session at once, this device, the record).
export const navbarLayoutSet = function navbarLayoutSet(patch) {
  const fns = this && this.context && this.context.functions
  if (!fns || typeof fns.homeLookSet !== 'function') return Promise.resolve(null)
  const comm = typeof fns.isCommHost === 'function' && !!this.call('isCommHost')
  const p = patch && typeof patch === 'object' ? patch : {}
  const change = {}
  if (['top', 'left', 'right', 'bottom'].includes(p.position)) {
    change[comm ? 'commNavbarPosition' : 'navbarPosition'] = p.position
  }
  if (typeof p.expanded === 'boolean') {
    change[comm ? 'commNavbarExpanded' : 'navbarExpanded'] = p.expanded
  }
  if (!Object.keys(change).length) return Promise.resolve(null)
  return this.call('homeLookSet', change)
}

// A name as a narrow rail shows it: the first letter of its first two words
// (the workspace switcher's monogram — the rail has no room for the name);
// the name itself anywhere else. Latin letters upper-cased only — Georgian
// Mkhedruli has no case, and toUpperCase() turns it into Mtavruli. One rule
// for every host's switcher (the shared header's and the workspace shell's
// own label override).
export const navbarRailLabel = function navbarRailLabel(name, root) {
  const text = String(name || '')
  const nav = (this && typeof this.call === 'function' && this.call('navbarLayoutRead', root)) || {}
  if (nav.railMode !== 'narrow') return text
  return text
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => Array.from(w)[0] || '')
    .join('')
    .replace(/[a-z]/g, (c) => c.toUpperCase())
}

// THE RAIL'S OUTER EDGE — where a menu or a tooltip opened from a control on
// a left or right rail anchors: the rail's own left and right, the control's
// own top and bottom (`trigger`, else the calling element). A panel placed
// beside that rect starts past the rail, never over it: anchored to the
// control alone, the org switcher's menu covered a wide rail's far half
// (measured on dev e6f222e23) and the apps launcher, anchored to the whole
// tab column, opened above it squeezed to 244px (dev 579269d80). null off a
// rail, and for a control that is not on the navbar.
export const navbarRailRect = function navbarRailRect(trigger) {
  const t = trigger || this
  if (!this || typeof this.call !== 'function' || !t) return null
  const nav = this.call('navbarLayoutRead', this.state && this.state.root) || {}
  if (!nav.rail) return null
  const bar = typeof t.lookup === 'function' ? t.lookup('AppHeader') : null
  const box = (n) =>
    n && typeof n.getBoundingClientRect === 'function' ? n.getBoundingClientRect() : null
  const edge = box(bar && bar.node)
  const at = box(t.node)
  if (!edge || !at) return null
  return {
    left: edge.left,
    right: edge.right,
    width: edge.width,
    top: at.top,
    bottom: at.bottom,
    height: at.height
  }
}

// A MENU OPENED FROM THE NAVBAR — the anchor half of an openDropdown call;
// the calling element is the control that opens it. `placement` is the
// menu's own side under a top bar, `anchorEl` what it anchors on there when
// not the control itself (the launcher centres under the whole tab pill).
// On a left or right rail the menu opens beside the rail, towards the page,
// on the rail's outer edge (navbarRailRect): its top on the control's in the
// rail's upper half, its foot on the control's in the lower half — a control
// near the foot of the rail opens upwards, one near the head downwards. The
// control keeps the focus a close returns. A top bar, a bottom bar (the
// plugin flips its menus above it) and a control off the navbar keep the
// opener's own anchor and side.
export const navbarMenuAnchor = function navbarMenuAnchor(placement, anchorEl) {
  const own = { anchorEl: anchorEl || this, placement }
  const rect = this && typeof this.call === 'function' ? this.call('navbarRailRect') : null
  if (!rect) return own
  const nav = this.call('navbarLayoutRead', this.state && this.state.root) || {}
  const side = nav.position === 'right' ? 'left' : 'right'
  const vh = typeof window !== 'undefined' ? window.innerHeight : 0
  const lower = rect.top + rect.height / 2 > vh / 2
  return { anchorRect: rect, restoreEl: this, placement: `${side}-${lower ? 'end' : 'start'}` }
}

// THE SIDE A NAVBAR CONTROL'S TOOLTIP OPENS TO — away from the bar, into the
// page (Nika 2026-10-25: "when its left sidebar show tooltips on right and so
// on"): under a top bar (as ever), above a bottom bar, to the right of a left
// rail, to the left of a right rail. The calling element is the control; one
// that is not on the navbar keeps the plugin's own side (under it).
export const navbarTooltipPlacement = function navbarTooltipPlacement() {
  if (!this || typeof this.call !== 'function') return 'bottom'
  const onBar = typeof this.lookup === 'function' && !!this.lookup('AppHeader')
  const nav = onBar ? this.call('navbarLayoutRead', this.state && this.state.root) || {} : {}
  return { left: 'right', right: 'left', bottom: 'top' }[nav.position] || 'bottom'
}

// The rail's own toggle: the names beside the glyphs, or the glyphs alone.
export const navbarRailToggle = function navbarRailToggle() {
  if (!this || typeof this.call !== 'function') return Promise.resolve(null)
  const root = this.state && this.state.root
  const now = this.call('navbarLayoutRead', root) || {}
  if (!now.rail) return Promise.resolve(null)
  return this.call('navbarLayoutSet', { expanded: !now.expanded })
}
